import React, { useEffect, useState } from 'react';
import { RefreshCw, Package, Mail, CheckCircle, CreditCard, Truck, AlertTriangle, ShieldCheck, Clock } from 'lucide-react';
import MainNavbar from '../../components/layout/MainNavbar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const sections = [
  {
    icon: Package,
    title: '1. Eligibility',
    items: [
      'Item must be unused, in original condition with tags, manuals, and packaging intact.',
      'Proof of purchase (order ID, invoice) is required.',
      'Non‑returnable: perishable goods, gift cards, downloadable software, intimate or sanitary goods, items marked "final sale", and products customized for you.',
    ],
  },
  {
    icon: Mail,
    title: '2. How to Initiate a Return',
    items: [
      'Email returns@yourbusiness.com within the return window with your order ID and reason.',
      "We'll share a return shipping label or pickup schedule where available.",
      'Pack the item securely; include all accessories and invoice.',
    ],
  },
  {
    icon: CheckCircle,
    title: '3. Inspection & Approval',
    items: [
      "After the item reaches our facility, allow 2–4 business days for quality check. We'll notify you of approval or rejection.",
    ],
  },
  {
    icon: CreditCard,
    title: '4. Refunds',
    items: [
      'Approved refunds are issued to the original payment method.',
      'UPI/Wallet/Card: typically 3–7 business days post‑approval; Net Banking: up to 7–10 days (bank dependent).',
      'COD orders are refunded to bank account/UPI as provided by you; we do not issue cash refunds.',
    ],
  },
  {
    icon: RefreshCw,
    title: '5. Exchanges',
    items: [
      "Exchanges are subject to stock availability. If the item is unavailable, we'll process a refund or store credit.",
    ],
  },
  {
    icon: Truck,
    title: '6. Shipping Costs',
    items: [
      'If the return is due to a defect, wrong item, or damage in transit, we cover return shipping.',
      'For other reasons (size, preference), return shipping may be deducted from your refund.',
    ],
  },
  {
    icon: AlertTriangle,
    title: '7. Damaged or Missing Items',
    items: [
      'Report damage or missing parts within 48 hours of delivery with photos/videos so we can assist promptly.',
    ],
  },
  {
    icon: ShieldCheck,
    title: '8. Warranty Claims',
    items: [
      "For manufacturer warranty claims, we may connect you with the brand's authorized service center. Keep your invoice handy.",
    ],
  },
];

const ReturnAndRefundPage = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  return (
    <>
      <div
        className={`min-h-screen relative overflow-hidden transition-colors duration-500 border-1 border-muted-foreground/20 ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] text-slate-100'
            : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-slate-900'
        }`}
      >
        <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Return & Refund Policy" />

        <div className="absolute inset-0 pointer-events-none">
          {theme === 'dark' ? (
            <>
              <div className="absolute -top-24 -left-24 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/3 right-10 w-72 h-72 bg-indigo-500/30 rounded-full blur-[140px] animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-[160px] animate-pulse delay-500" />
            </>
          ) : (
            <>
              <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-indigo-200/40 rounded-full blur-3xl animate-pulse delay-500" />
            </>
          )}
        </div>

        <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-10">
            <header className="space-y-4">
              <div className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full ${theme === 'dark' ? 'bg-slate-900 border-1 border-muted-foreground/20 text-slate-100' : 'bg-white border-1 border-muted-foreground/20 text-slate-700'}`}>
                <RefreshCw className="h-4 w-4" />
                Return & Refund Policy
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold">Return & Refund Policy</h1>
              <p className={`text-base sm:text-lg ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                TrackYourWheelz provides a clear return and refund process to ensure your satisfaction. Review the eligibility criteria, return process, and refund timelines below.
              </p>
            </header>

            <section className={`rounded-xl border-1 border-muted-foreground/20 ${theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'} p-6 space-y-3`}>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Return Window
              </h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                <strong>7 days from delivery</strong> unless a different period is mentioned on the product page.
              </p>
            </section>

            <section className="space-y-6">
              {sections.map((section) => (
                <Card
                  key={section.title}
                  className={`border ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'}`}
                >
                  <CardHeader className="flex flex-row items-start gap-3 p-6 pb-3">
                    <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
                      <section.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold">{section.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className={`p-6 pt-0 space-y-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                    <ul className={`list-disc ps-5 space-y-1 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                      {section.items.map((item, idx) => (
                        <li key={idx} className="text-sm leading-relaxed">{item}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </section>

            <footer className={`rounded-xl border ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'} p-6 space-y-4`}>
              <h2 className="text-lg font-semibold">Need help with returns or refunds?</h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                Our returns team can assist with return requests, refund status, and any questions about the process.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => window.open('mailto:returns@yourbusiness.com', '_blank')} className="w-full sm:w-48" variant="outline">
                  Email Returns Team
                </Button>
                <Button variant="outline" onClick={() => window.history.back()} className="w-full sm:w-48">
                  Go Back
                </Button>
              </div>
            </footer>
          </div>
        </main>
      </div>
    </>
  );
};

export default ReturnAndRefundPage;
